import React from 'react';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';

// Icons
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Mock data for the dashboard
const mockStats = {
    todaySales: {
        amount: 125750.00,
        invoiceCount: 18,
        trend: 12.5, // percentage increase from yesterday
    },
    weekSales: {
        amount: 875420.00,
        invoiceCount: 124,
        trend: 8.3,
    },
    monthSales: {
        amount: 3542680.00,
        invoiceCount: 487,
        trend: -2.1, // negative means decrease
    },
    pendingPayments: {
        amount: 245000.00,
        count: 23,
    },
    activeCustomers: 156,
    registeredVehicles: 89,
    lowStockItems: 7,
};

const mockRecentInvoices = [
    { id: 'INV-2026-0018', customer: 'John Silva', amount: 12500.00, status: 'CONFIRMED', paymentStatus: 'FULLY_PAID', time: '10:45 AM' },
    { id: 'INV-2026-0017', customer: 'Mary Fernando', amount: 8750.00, status: 'CONFIRMED', paymentStatus: 'PARTIALLY_PAID', time: '10:15 AM' },
    { id: 'INV-2026-0016', customer: 'Walk-in customer', amount: 3200.00, status: 'CONFIRMED', paymentStatus: 'FULLY_PAID', time: '09:50 AM' },
    { id: 'INV-2026-0015', customer: 'David Perera', amount: 45000.00, status: 'CONFIRMED', paymentStatus: 'UNPAID', time: '09:30 AM' },
    { id: 'INV-2026-0014', customer: 'Sarah Kumar', amount: 6800.00, status: 'DRAFT', paymentStatus: 'N/A', time: '09:10 AM' },
];

const mockLowStockItems = [
    { name: 'Engine Oil 5W-30', currentStock: 3, minStock: 10, location: 'Main Store' },
    { name: 'Brake Pads (Front)', currentStock: 2, minStock: 8, location: 'Main Store' },
    { name: 'Air Filter - Toyota', currentStock: 1, minStock: 5, location: 'Warehouse' },
    { name: 'Transmission Fluid', currentStock: 4, minStock: 12, location: 'Main Store' },
];

const mockTopServices = [
    { name: 'Full Service', count: 45, revenue: 675000 },
    { name: 'Oil Change', count: 78, revenue: 312000 },
    { name: 'Brake Service', count: 32, revenue: 480000 },
    { name: 'Tire Rotation', count: 56, revenue: 168000 },
];

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'primary', onClick }) => {
    const isPositiveTrend = trend > 0;

    return (
        <Card sx={{ height: '100%' }}>
            <CardActionArea onClick={onClick} sx={{ height: '100%' }} disabled={!onClick}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                {title}
                            </Typography>
                            <Typography variant="h5" component="div" fontWeight="bold">
                                {value}
                            </Typography>
                            {subtitle && (
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: `${color}.light`,
                                borderRadius: 2,
                                p: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Icon sx={{ color: `${color}.main`, fontSize: 28 }} />
                        </Box>
                    </Box>
                    {trend !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            {isPositiveTrend ? (
                                <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18, mr: 0.5 }} />
                            ) : (
                                <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18, mr: 0.5 }} />
                            )}
                            <Typography
                                variant="body2"
                                sx={{ color: isPositiveTrend ? 'success.main' : 'error.main' }}
                            >
                                {Math.abs(trend)}% from previous period
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

// Payment Status Chip
const PaymentStatusChip = ({ status }) => {
    const config = {
        'N/A': { label: 'N/A', color: 'default' },
        'UNPAID': { label: 'Unpaid', color: 'warning' },
        'PARTIALLY_PAID': { label: 'Partial', color: 'info' },
        'FULLY_PAID': { label: 'Paid', color: 'success' },
    };

    const { label, color } = config[status] || config['N/A'];

    return <Chip label={label} color={color} size="small" variant="outlined" />;
};

const Dashboard = () => {
    const navigate = useNavigate();

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Welcome back! Here's an overview of your business today.
                </Typography>
            </Box>

            {/* Quick Action Buttons */}
            <Box sx={{ mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<PointOfSaleIcon />}
                    onClick={() => navigate('/sales/pos/start')}
                    sx={{ mr: 2 }}
                >
                    New Sale
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={() => navigate('/customers/add')}
                    sx={{ mr: 2 }}
                >
                    Add Customer
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<InventoryIcon />}
                    onClick={() => navigate('/inventory/stocks/add')}
                >
                    Add Stock
                </Button>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Today's Sales"
                        value={`Rs. ${formatCurrency(mockStats.todaySales.amount)}`}
                        subtitle={`${mockStats.todaySales.invoiceCount} invoices`}
                        icon={ReceiptIcon}
                        trend={mockStats.todaySales.trend}
                        color="primary"
                        onClick={() => navigate('/sales/invoices')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="This Week"
                        value={`Rs. ${formatCurrency(mockStats.weekSales.amount)}`}
                        subtitle={`${mockStats.weekSales.invoiceCount} invoices`}
                        icon={TrendingUpIcon}
                        trend={mockStats.weekSales.trend}
                        color="success"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="This Month"
                        value={`Rs. ${formatCurrency(mockStats.monthSales.amount)}`}
                        subtitle={`${mockStats.monthSales.invoiceCount} invoices`}
                        icon={TrendingUpIcon}
                        trend={mockStats.monthSales.trend}
                        color="info"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Pending Payments"
                        value={`Rs. ${formatCurrency(mockStats.pendingPayments.amount)}`}
                        subtitle={`${mockStats.pendingPayments.count} invoices`}
                        icon={PaymentIcon}
                        color="warning"
                        onClick={() => navigate('/payments')}
                    />
                </Grid>
            </Grid>

            {/* Secondary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        title="Active Customers"
                        value={mockStats.activeCustomers}
                        icon={PeopleIcon}
                        color="primary"
                        onClick={() => navigate('/customers')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        title="Registered Vehicles"
                        value={mockStats.registeredVehicles}
                        icon={DirectionsCarIcon}
                        color="info"
                        onClick={() => navigate('/vehicles')}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        title="Low Stock Alerts"
                        value={mockStats.lowStockItems}
                        subtitle="Items need restocking"
                        icon={WarningAmberIcon}
                        color="error"
                        onClick={() => navigate('/inventory/stocks')}
                    />
                </Grid>
            </Grid>

            {/* Tables Section */}
            <Grid container spacing={3}>
                {/* Recent Invoices */}
                <Grid item xs={12} lg={7}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Recent Invoices</Typography>
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/sales/invoices')}
                            >
                                View All
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Invoice</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Payment</TableCell>
                                        <TableCell>Time</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mockRecentInvoices.map((invoice) => (
                                        <TableRow
                                            key={invoice.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/sales/invoices/${invoice.id}`)}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {invoice.id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{invoice.customer}</TableCell>
                                            <TableCell align="right">
                                                Rs. {formatCurrency(invoice.amount)}
                                            </TableCell>
                                            <TableCell>
                                                <PaymentStatusChip status={invoice.paymentStatus} />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {invoice.time}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Low Stock Items */}
                <Grid item xs={12} lg={5}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">
                                <WarningAmberIcon sx={{ color: 'warning.main', mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                                Low Stock Items
                            </Typography>
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate('/inventory/stocks')}
                            >
                                View All
                            </Button>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Item</TableCell>
                                        <TableCell>Location</TableCell>
                                        <TableCell align="right">Stock</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {mockLowStockItems.map((item, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {item.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.location}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                    <Typography
                                                        variant="body2"
                                                        color="error.main"
                                                        fontWeight="bold"
                                                        sx={{ mr: 1 }}
                                                    >
                                                        {item.currentStock}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        / {item.minStock}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Top Services */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Top Services (This Month)
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            {mockTopServices.map((service, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="body2">{service.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {service.count} orders • Rs. {formatCurrency(service.revenue)}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(service.count / mockTopServices[0].count) * 100}
                                        sx={{ height: 8, borderRadius: 4 }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>
                </Grid>

                {/* Quick Stats Summary */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Business Summary
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'success.lighter', borderRadius: 2 }}>
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold" color="success.main">
                                        Rs. {formatCurrency(3297680)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Collected (This Month)
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'warning.lighter', borderRadius: 2 }}>
                                    <PaymentIcon sx={{ color: 'warning.main', fontSize: 32, mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                                        Rs. {formatCurrency(245000)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Outstanding Balance
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'info.lighter', borderRadius: 2 }}>
                                    <PeopleIcon sx={{ color: 'info.main', fontSize: 32, mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold" color="info.main">
                                        24
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        New Customers (This Month)
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'primary.lighter', borderRadius: 2 }}>
                                    <DirectionsCarIcon sx={{ color: 'primary.main', fontSize: 32, mb: 1 }} />
                                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                                        487
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Vehicles Serviced
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;
